import { type Color, ColorScheme } from '@blocksuite/affine-model';
import { FeatureFlagService } from '@blocksuite/affine-shared/services';
import {
  canvasArrivalDelay,
  isStreamLainMotionEnabled,
  requestConnectedFrame,
  streamLainAnimationController,
  type StreamLainAnimationHandle,
  subscribeStreamLainMotionPolicy,
} from '@blocksuite/affine-shared/utils';
import { DisposableGroup } from '@blocksuite/global/disposable';
import type { IBound } from '@blocksuite/global/gfx';
import { getBoundWithRotation, intersects } from '@blocksuite/global/gfx';
import type { BlockStdScope } from '@blocksuite/std';
import type {
  GfxCompatibleInterface,
  GridManager,
  LayerManager,
  SurfaceBlockModel,
  Viewport,
} from '@blocksuite/std/gfx';
import {
  cancelGfxViewportFrame,
  measureGfxPerformance,
  scheduleGfxViewportFrame,
} from '@blocksuite/std/gfx';
import last from 'lodash-es/last';
import { Subject } from 'rxjs';

import type { SurfaceElementModel } from '../element-model/base.js';
import { ElementRendererIdentifier } from '../extensions/element-renderer.js';
import { RoughCanvas } from '../utils/rough/canvas.js';
import { canvasBackingLength } from './canvas-backing-size.js';
import type { ElementRenderer } from './elements/index.js';
import type { Overlay } from './overlay.js';

type EnvProvider = {
  generateColorProperty: (color: Color, fallback?: Color) => string;
  getColorScheme: () => ColorScheme;
  getColorValue: (color: Color, fallback?: Color, real?: boolean) => string;
  getPropertyValue: (property: string) => string;
  selectedElements?: () => string[];
};

type RendererOptions = {
  std: BlockStdScope;
  viewport: Viewport;
  layerManager: LayerManager;
  provider?: Partial<EnvProvider>;
  enableStackingCanvas?: boolean;
  onStackingCanvasCreated?: (canvas: HTMLCanvasElement) => void;
  gridManager: GridManager;
  surfaceModel: SurfaceBlockModel;
};

function elementPerformanceDetail(element: SurfaceElementModel) {
  const diagnostic = element as SurfaceElementModel & {
    labelXYWH?: unknown;
    path?: unknown[];
    rough?: boolean;
    text?: { length?: number; toString?: () => string };
  };
  const textLength =
    typeof diagnostic.text?.length === 'number'
      ? diagnostic.text.length
      : diagnostic.text?.toString?.().length;

  return {
    elementId: element.id,
    elementType: element.type,
    x: element.x,
    y: element.y,
    width: element.w,
    height: element.h,
    pathPointCount: Array.isArray(diagnostic.path)
      ? diagnostic.path.length
      : null,
    rough: diagnostic.rough ?? null,
    hasLabel: diagnostic.labelXYWH !== undefined,
    labelTextLength: textLength ?? null,
  };
}

type CanvasArrival = {
  value: number;
  ids: Set<string>;
  handle?: StreamLainAnimationHandle;
};

export class CanvasRenderer {
  private _container!: HTMLElement;

  private readonly _arrivalSeen = new Set<string>();
  private readonly _arrivals = new Map<string, CanvasArrival>();
  private _arrivalBatch: CanvasArrival | undefined;
  private _arrivalTimer: number | undefined;
  private _arrivalDeadline: number | undefined;
  private readonly _arrivalPreference = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  private _clearArrivalTimer() {
    window.clearTimeout(this._arrivalTimer);
    this._arrivalTimer = undefined;
    this._arrivalDeadline = undefined;
  }

  private _waitForArrival(delay: number, now: number) {
    const deadline = now + delay;
    if (
      this._arrivalDeadline !== undefined &&
      this._arrivalDeadline <= deadline
    )
      return;
    this._clearArrivalTimer();
    this._arrivalDeadline = deadline;
    this._arrivalTimer = window.setTimeout(() => {
      this._arrivalTimer = undefined;
      this._arrivalDeadline = undefined;
      this.refresh('canvas-arrival-ready');
    }, delay);
  }

  private _arrivalOpacity(
    element: SurfaceElementModel,
    ctx: CanvasRenderingContext2D
  ): number {
    // Export/offscreen rendering always uses the full model opacity.
    if (
      !this._container?.isConnected ||
      (ctx.canvas !== this.canvas && !this._stackingCanvas.includes(ctx.canvas))
    )
      return 1;
    if (
      !isStreamLainMotionEnabled('canvas') ||
      this._arrivalPreference.matches
    ) {
      canvasArrivalDelay(element.id, Infinity);
      this._arrivalSeen.add(element.id);
      return 1;
    }
    const now = performance.now();
    const delay = canvasArrivalDelay(element.id, now);
    if (delay > 0) {
      this._waitForArrival(delay, now);
      return 0;
    }
    const current = this._arrivals.get(element.id);
    if (current) return current.value;
    if (this._arrivalSeen.has(element.id)) return 1;
    this._arrivalSeen.add(element.id);
    let arrival = this._arrivalBatch;
    if (!arrival) {
      const batch: CanvasArrival = { value: 0, ids: new Set() };
      arrival = this._arrivalBatch = batch;
      // All objects arriving in one render share a controller channel and refresh.
      const token = this.canvas.ownerDocument.createElement('span');
      batch.handle = streamLainAnimationController.animateProgress(
        token,
        value => {
          batch.value = value;
          this.refresh('canvas-arrival');
        },
        'canvasEnter',
        'canvas'
      );
      void batch.handle.finished.then(() => {
        let changed = false;
        for (const id of batch.ids)
          if (this._arrivals.get(id) === batch) {
            this._arrivals.delete(id);
            changed = true;
          }
        if (changed) this.refresh('canvas-arrival-settled');
      });
    }
    arrival.ids.add(element.id);
    this._arrivals.set(element.id, arrival);
    return arrival.value;
  }

  private readonly _disposables = new DisposableGroup();

  private readonly _turboEnabled: () => boolean;

  private readonly _overlays = new Set<Overlay>();

  private _refreshRafId: number | null = null;

  private _viewportRefreshScheduled = false;

  private readonly _refreshReasons = new Set<string>();

  private _stackingCanvas: HTMLCanvasElement[] = [];

  canvas: HTMLCanvasElement;

  ctx: CanvasRenderingContext2D;

  std: BlockStdScope;

  grid: GridManager;

  layerManager: LayerManager;

  provider: Partial<EnvProvider>;

  stackingCanvasUpdated = new Subject<{
    canvases: HTMLCanvasElement[];
    added: HTMLCanvasElement[];
    removed: HTMLCanvasElement[];
  }>();

  usePlaceholder = false;

  viewport: Viewport;

  get stackingCanvas() {
    return this._stackingCanvas;
  }

  constructor(options: RendererOptions) {
    const canvas = document.createElement('canvas');

    this.canvas = canvas;
    this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;
    this.std = options.std;
    this.viewport = options.viewport;
    this.layerManager = options.layerManager;
    this.grid = options.gridManager;
    this.provider = options.provider ?? {};

    this._turboEnabled = () => {
      const featureFlagService = options.std.get(FeatureFlagService);
      return featureFlagService.getFlag('enable_turbo_renderer');
    };

    this._initViewport();

    options.enableStackingCanvas = options.enableStackingCanvas ?? false;
    if (options.enableStackingCanvas) {
      this._initStackingCanvas(options.onStackingCanvasCreated);
    }

    const settleArrivals = () => {
      if (
        isStreamLainMotionEnabled('canvas') &&
        !this._arrivalPreference.matches
      )
        return;
      this._clearArrivalTimer();
      this.refresh('canvas-arrival-motion-disabled');
    };
    this._arrivalPreference.addEventListener('change', settleArrivals);
    this._disposables.add(() =>
      this._arrivalPreference.removeEventListener('change', settleArrivals)
    );
    this._disposables.add(subscribeStreamLainMotionPolicy(settleArrivals));

    this._watchSurface(options.surfaceModel);
  }

  /**
   * Specifying the actual size gives better results and more consistent behavior across browsers.
   *
   * Make sure the main canvas and the offscreen canvas or layer canvas are the same size.
   *
   * It is not recommended to set width and height to 100%.
   */
  private _canvasSizeUpdater(dpr = window.devicePixelRatio) {
    const { width, height, viewScale } = this.viewport;
    const actualWidth = Math.ceil(width * dpr);
    const actualHeight = Math.ceil(height * dpr);

    const backing = (canvas: HTMLCanvasElement) => ({
      width: canvasBackingLength(canvas.width, actualWidth),
      height: canvasBackingLength(canvas.height, actualHeight),
    });

    return {
      filter(canvas: HTMLCanvasElement) {
        const { width, height } = backing(canvas);
        return canvas.width !== width || canvas.height !== height;
      },
      update(canvas: HTMLCanvasElement) {
        const { width, height } = backing(canvas);
        // The box follows the backing store, one canvas pixel per device pixel; what lies beyond
        // the viewport is clipped by it.
        canvas.style.width = `${width / dpr}px`;
        canvas.style.height = `${height / dpr}px`;
        canvas.style.transform = `scale(${1 / viewScale})`;
        canvas.style.transformOrigin = `top left`;
        if (canvas.width !== width) canvas.width = width;
        if (canvas.height !== height) canvas.height = height;
      },
    };
  }

  private _initStackingCanvas(onCreated?: (canvas: HTMLCanvasElement) => void) {
    const layer = this.layerManager;
    const updateStackingCanvasSize = (canvases: HTMLCanvasElement[]) => {
      this._stackingCanvas = canvases;

      const sizeUpdater = this._canvasSizeUpdater();

      canvases.filter(sizeUpdater.filter).forEach(sizeUpdater.update);
    };
    const updateStackingCanvas = () => {
      /**
       * we already have a main canvas, so the last layer should be skipped
       */
      const canvasLayers = layer.getCanvasLayers().slice(0, -1);
      const canvases = [];
      const currentCanvases = this._stackingCanvas;
      const lastLayer = last(this.layerManager.layers);
      const maximumZIndex = lastLayer
        ? lastLayer.zIndex + lastLayer.elements.length + 1
        : 1;

      this.canvas.style.zIndex = maximumZIndex.toString();
      for (let i = 0; i < canvasLayers.length; ++i) {
        const layer = canvasLayers[i];
        const created = i < currentCanvases.length;
        const canvas = created
          ? currentCanvases[i]
          : document.createElement('canvas');

        if (!created) {
          onCreated?.(canvas);
        }

        canvas.dataset.layerId = `[${layer.indexes[0]}--${layer.indexes[1]}]`;
        canvas.style.zIndex = layer.zIndex.toString();
        canvases.push(canvas);
      }

      this._stackingCanvas = canvases;
      updateStackingCanvasSize(canvases);

      if (currentCanvases.length !== canvases.length) {
        const diff = canvases.length - currentCanvases.length;
        const payload: {
          canvases: HTMLCanvasElement[];
          removed: HTMLCanvasElement[];
          added: HTMLCanvasElement[];
        } = {
          canvases,
          removed: [],
          added: [],
        };

        if (diff > 0) {
          payload.added = canvases.slice(-diff);
        } else {
          payload.removed = currentCanvases.slice(diff);
        }

        this.stackingCanvasUpdated.next(payload);
      }

      this.refresh('layer-structure');
    };

    this._disposables.add(
      this.layerManager.slots.layerUpdated.subscribe(() => {
        updateStackingCanvas();
      })
    );

    updateStackingCanvas();
  }

  private _initViewport() {
    let sizeUpdatedRafId: number | null = null;

    this._disposables.add(
      this.viewport.viewportUpdated.subscribe(() => {
        this._scheduleViewportRefresh();
      })
    );

    this._disposables.add(
      this.viewport.sizeUpdated.subscribe(() => {
        if (sizeUpdatedRafId) return;
        sizeUpdatedRafId = requestConnectedFrame(() => {
          sizeUpdatedRafId = null;
          this._resetSize();
          this._render();
          this.refresh('viewport-size');
        }, this._container);
      })
    );

    this._disposables.add(
      this.viewport.zooming$.subscribe(isZooming => {
        const shouldRenderPlaceholders = this._turboEnabled() && isZooming;

        if (this.usePlaceholder !== shouldRenderPlaceholders) {
          this.usePlaceholder = shouldRenderPlaceholders;
          this.refresh('zoom-placeholder');
        }
      })
    );

    this.usePlaceholder = false;
  }

  private _render(trigger = 'direct') {
    measureGfxPerformance(
      this.std,
      'canvas-render',
      () => this._renderCore(),
      () =>
        this.layerManager
          .getCanvasLayers()
          .reduce((count, layer) => count + layer.elements.length, 0),
      () => ({
        trigger,
        viewportState: this.viewport.serializeRecord(),
        viewportX: this.viewport.viewportX,
        viewportY: this.viewport.viewportY,
        zoom: this.viewport.zoom,
      })
    );
  }

  private _renderCore() {
    this._arrivalBatch = undefined;
    const { viewportBounds, zoom } = this.viewport;
    const { ctx } = this;
    const dpr = window.devicePixelRatio;
    const scale = zoom * dpr;
    const matrix = new DOMMatrix().scaleSelf(scale);
    /**
     * if a layer does not have a corresponding canvas
     * its element will be add to this array and drawing on the
     * main canvas
     */
    let fallbackElement: SurfaceElementModel[] = [];

    this.layerManager.getCanvasLayers().forEach((layer, idx) => {
      if (!this._stackingCanvas[idx]) {
        fallbackElement = fallbackElement.concat(layer.elements);
        return;
      }

      const canvas = this._stackingCanvas[idx];
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
      const rc = new RoughCanvas(ctx.canvas);
      let renderedElements = 0;
      measureGfxPerformance(
        this.std,
        'canvas-layer-render',
        () => {
          measureGfxPerformance(
            this.std,
            'canvas-layer-setup',
            () => {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.save();
              ctx.setTransform(matrix);
            },
            undefined,
            () => ({ canvas: 'stacking', layerIndex: idx })
          );
          renderedElements = this._renderByBound(
            ctx,
            matrix,
            rc,
            viewportBounds,
            layer.elements
          );
        },
        () => renderedElements,
        () => ({
          canvas: 'stacking',
          canvasHeight: canvas.height,
          canvasWidth: canvas.width,
          layerElements: layer.elements.length,
          layerIndex: idx,
        })
      );
    });

    let mainRenderedElements = 0;
    measureGfxPerformance(
      this.std,
      'canvas-layer-render',
      () => {
        measureGfxPerformance(
          this.std,
          'canvas-layer-setup',
          () => {
            ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            ctx.save();
            ctx.setTransform(matrix);
          },
          undefined,
          () => ({ canvas: 'main', layerIndex: -1 })
        );
        mainRenderedElements = this._renderByBound(
          ctx,
          matrix,
          new RoughCanvas(ctx.canvas),
          viewportBounds,
          fallbackElement,
          true
        );
      },
      () => mainRenderedElements,
      () => ({
        canvas: 'main',
        canvasHeight: this.canvas.height,
        canvasWidth: this.canvas.width,
        layerElements: fallbackElement.length,
        layerIndex: -1,
      })
    );
  }

  private _renderByBound(
    ctx: CanvasRenderingContext2D | null,
    matrix: DOMMatrix,
    rc: RoughCanvas,
    bound: IBound,
    surfaceElements?: SurfaceElementModel[],
    overLay: boolean = false
  ) {
    if (!ctx) return 0;

    const elements =
      surfaceElements ??
      (this.grid.search(bound, {
        filter: ['canvas', 'local'],
      }) as SurfaceElementModel[]);

    let renderedElements = 0;
    for (const element of elements) {
      const display = (element.display ?? true) && !element.hidden;
      let visible = false;
      visible = measureGfxPerformance(
        this.std,
        'canvas-element-visibility',
        () => {
          visible = display && intersects(getBoundWithRotation(element), bound);
          return visible;
        },
        () => 1,
        () => ({
          elementId: element.id,
          elementType: element.type,
          display,
          visible,
        })
      );
      if (visible) {
        if (
          this.usePlaceholder &&
          !(element as GfxCompatibleInterface).forceFullRender
        ) {
          measureGfxPerformance(
            this.std,
            'canvas-element-render',
            () => {
              ctx.save();
              ctx.fillStyle = 'rgba(200, 200, 200, 0.5)';
              const drawX = element.x - bound.x;
              const drawY = element.y - bound.y;
              ctx.fillRect(drawX, drawY, element.w, element.h);
              ctx.restore();
            },
            () => 1,
            () => ({
              canvas: overLay ? 'main' : 'stacking',
              ...elementPerformanceDetail(element),
              renderer: 'placeholder',
            })
          );
          renderedElements += 1;
        } else {
          const renderFn = this.std.getOptional<ElementRenderer>(
            ElementRendererIdentifier(element.type)
          );

          if (!renderFn) continue;
          measureGfxPerformance(
            this.std,
            'canvas-element-render',
            () => {
              ctx.save();
              try {
                ctx.globalAlpha =
                  (element.opacity ?? 1) * this._arrivalOpacity(element, ctx);
                const dx = element.x - bound.x;
                const dy = element.y - bound.y;
                renderFn(
                  element,
                  ctx,
                  matrix.translate(dx, dy),
                  this,
                  rc,
                  bound
                );
              } finally {
                ctx.restore();
              }
            },
            () => 1,
            () => ({
              canvas: overLay ? 'main' : 'stacking',
              ...elementPerformanceDetail(element),
              renderer: renderFn.name || 'anonymous',
            })
          );
          renderedElements += 1;
        }
      }
    }

    if (overLay) {
      let overlayIndex = 0;
      for (const overlay of this._overlays) {
        const currentOverlayIndex = overlayIndex++;
        measureGfxPerformance(
          this.std,
          'canvas-overlay-render',
          () => {
            ctx.save();
            try {
              ctx.translate(-bound.x, -bound.y);
              overlay.render(ctx, rc);
            } finally {
              ctx.restore();
            }
          },
          () => 1,
          () => ({ overlayIndex: currentOverlayIndex })
        );
      }
    }

    ctx.restore();
    return renderedElements;
  }

  private _resetSize() {
    const sizeUpdater = this._canvasSizeUpdater();

    sizeUpdater.update(this.canvas);

    this._stackingCanvas.forEach(sizeUpdater.update);
    this.refresh('canvas-resize');
  }

  private _watchSurface(surfaceModel: SurfaceBlockModel) {
    this._disposables.add(
      surfaceModel.elementAdded.subscribe(() => this.refresh('element-added'))
    );
    this._disposables.add(
      surfaceModel.elementRemoved.subscribe(() =>
        this.refresh('element-removed')
      )
    );
    this._disposables.add(
      surfaceModel.localElementAdded.subscribe(() =>
        this.refresh('local-element-added')
      )
    );
    this._disposables.add(
      surfaceModel.localElementDeleted.subscribe(() =>
        this.refresh('local-element-deleted')
      )
    );
    this._disposables.add(
      surfaceModel.localElementUpdated.subscribe(() =>
        this.refresh('local-element-updated')
      )
    );

    this._disposables.add(
      surfaceModel.elementUpdated.subscribe(payload => {
        // ignore externalXYWH update cause it's updated by the renderer
        if (payload.props['externalXYWH']) return;
        this.refresh('element-updated');
      })
    );
  }

  addOverlay(overlay: Overlay) {
    overlay.setRenderer(this);
    this._overlays.add(overlay);
    this.refresh('overlay-added');
  }

  /**
   * Used to attach main canvas, main canvas will always exist
   * @param container
   */
  attach(container: HTMLElement) {
    this._container = container;
    container.append(this.canvas);

    this._resetSize();
    this.refresh('attach');
  }

  dispose(): void {
    this._clearArrivalTimer();
    for (const arrival of new Set(this._arrivals.values()))
      arrival.handle?.cancel();
    this._arrivals.clear();
    this._arrivalBatch = undefined;
    this._arrivalSeen.clear();
    cancelGfxViewportFrame(this.std, 'raster', this);
    this._overlays.forEach(overlay => overlay.dispose());
    this._overlays.clear();
    this._disposables.dispose();
  }

  generateColorProperty(color: Color, fallback?: Color) {
    return (
      this.provider.generateColorProperty?.(color, fallback) ?? 'transparent'
    );
  }

  getCanvasByBound(
    bound: IBound = this.viewport.viewportBounds,
    surfaceElements?: SurfaceElementModel[],
    canvas?: HTMLCanvasElement,
    clearBeforeDrawing?: boolean,
    withZoom?: boolean
  ): HTMLCanvasElement {
    canvas = canvas || document.createElement('canvas');

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== bound.w * dpr) canvas.width = bound.w * dpr;
    if (canvas.height !== bound.h * dpr) canvas.height = bound.h * dpr;

    canvas.style.width = `${bound.w}px`;
    canvas.style.height = `${bound.h}px`;

    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    const matrix = new DOMMatrix().scaleSelf(
      withZoom ? dpr * this.viewport.zoom : dpr
    );
    const rc = new RoughCanvas(canvas);

    if (clearBeforeDrawing) ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(matrix);

    this._renderByBound(ctx, matrix, rc, bound, surfaceElements);

    return canvas;
  }

  getColorScheme() {
    return this.provider.getColorScheme?.() ?? ColorScheme.Light;
  }

  getColorValue(color: Color, fallback?: Color, real?: boolean) {
    return (
      this.provider.getColorValue?.(color, fallback, real) ?? 'transparent'
    );
  }

  getPropertyValue(property: string) {
    return this.provider.getPropertyValue?.(property) ?? '';
  }

  refresh(reason = 'external') {
    this._refreshReasons.add(reason);
    measureGfxPerformance(
      this.std,
      'canvas-refresh-request',
      () => undefined,
      undefined,
      () => ({
        coalesced:
          this._refreshRafId !== null || this._viewportRefreshScheduled,
        reason,
      })
    );
    if (this._viewportRefreshScheduled) return;
    if (this._refreshRafId !== null) return;

    this._refreshRafId = requestConnectedFrame(() => {
      this._refreshRafId = null;
      const trigger = [...this._refreshReasons].sort().join(',');
      this._refreshReasons.clear();
      this._render(trigger);
    }, this._container);
  }

  private _scheduleViewportRefresh() {
    this._refreshReasons.add('viewport-update');
    measureGfxPerformance(
      this.std,
      'canvas-refresh-request',
      () => undefined,
      undefined,
      () => ({
        coalesced:
          this._viewportRefreshScheduled || this._refreshRafId !== null,
        reason: 'viewport-update',
      })
    );
    if (this._viewportRefreshScheduled) return;
    this._viewportRefreshScheduled = true;
    if (this._refreshRafId !== null) {
      cancelAnimationFrame(this._refreshRafId);
      this._refreshRafId = null;
    }
    scheduleGfxViewportFrame(this.std, 'raster', this, () => {
      this._viewportRefreshScheduled = false;
      if (!this._container.isConnected) return;
      const trigger = [...this._refreshReasons].sort().join(',');
      this._refreshReasons.clear();
      this._render(trigger);
    });
  }

  removeOverlay(overlay: Overlay) {
    if (!this._overlays.has(overlay)) {
      return;
    }

    overlay.setRenderer(null);
    this._overlays.delete(overlay);
    this.refresh('overlay-removed');
  }
}
